import React from 'react'
import { isNumericColumn } from '../detail/gantt-columns'
import { HEADER_H, INDENT_PX, ROW_H, type ColumnId } from '../lib/constants'
import { rowY } from '../lib/geometry'
import { printCellText, type PrintRow } from './print-data'
import type { PrintLabels } from './print-labels'
import type { PrintLayout, PrintPageRef } from './print-layout'
import { truncateToWidth } from './print-text'

export interface PrintGridProps {
  layout: PrintLayout
  page: PrintPageRef
  /** Already sliced to this page by `PrintPage` — row `i` of this array draws at `rowY(i)`. */
  rows: PrintRow[]
  labels: PrintLabels
  includeCritical: boolean
  /** Plain function, never the `useTranslation` hook — these components must render under SSR. */
  t: (key: string, opts?: any) => string
}

/** Left text inset inside a cell (mirrors `GanttRow`'s `px-2` + the name column's `paddingLeft: 8`). */
const CELL_PAD = 8
/** Horizontal space a cell reserves for padding when truncating, per spec §4.2. */
const TEXT_PAD = 12

const fit = (text: string, width: number, indent: number): string =>
  truncateToWidth(text, width - TEXT_PAD - indent)

/**
 * The left grid pane on paper: the same columns, text, indent and alignment `GanttRow` /
 * `GanttGridHeader` render on screen (spec §4.2), as flat SVG text. Health prints its label
 * rather than the badge icon; a milestone name keeps the diamond as a text prefix.
 */
const PrintGrid: React.FC<PrintGridProps> = ({ layout, page, rows, labels, includeCritical, t }) => {
  const bodyH = HEADER_H + rows.length * ROW_H
  const cols: { id: ColumnId; x: number; w: number }[] = []
  let cursor = 0
  layout.columns.forEach((id) => {
    const w = layout.columnWidths[id]
    cols.push({ id, x: cursor, w })
    cursor += w
  })

  return (
    <g data-print-grid={page.index}>
      {cols.map((c) => {
        const numeric = isNumericColumn(c.id)
        return (
          <g key={`head-${c.id}`}>
            <text
              x={numeric ? c.x + c.w - CELL_PAD : c.x + CELL_PAD}
              y={HEADER_H - CELL_PAD}
              textAnchor={numeric ? 'end' : undefined}
              className="text-xs font-medium fill-muted-foreground"
            >
              {fit(labels.columns[c.id], c.w, 0)}
            </text>
            <line x1={c.x + c.w} x2={c.x + c.w} y1={0} y2={bodyH} className="stroke-border" strokeWidth={1} />
          </g>
        )
      })}
      <line x1={0} x2={layout.gridWidth} y1={HEADER_H} y2={HEADER_H} className="stroke-border" strokeWidth={1} />

      {rows.map((row, i) => {
        const a = row.activity
        const top = rowY(i)
        return (
          <g key={row.id} data-print-row={row.id}>
            {cols.map((c) => {
              const numeric = isNumericColumn(c.id)
              const isName = c.id === 'name'
              const indent = isName ? row.depth * INDENT_PX : 0
              const text = isName ? `${a.isMilestone ? '◆ ' : ''}${a.name}` : printCellText(c.id, row, t, labels)
              // Single fill class per cell: two Tailwind fills on one element would resolve by
              // stylesheet order, not by the order they are written here.
              const fill = c.id === 'float' && includeCritical && a.isCritical && a.totalFloatDays === 0
                ? 'fill-destructive'
                : c.id === 'rowNumber' ? 'fill-muted-foreground' : 'fill-foreground'
              return (
                <text
                  key={c.id}
                  x={numeric ? c.x + c.w - CELL_PAD : c.x + CELL_PAD + indent}
                  y={top + ROW_H / 2}
                  textAnchor={numeric ? 'end' : undefined}
                  dominantBaseline="middle"
                  className={`text-xs ${fill}${a.isSummary && isName ? ' font-semibold' : ''}`}
                >
                  {fit(text, c.w, indent)}
                </text>
              )
            })}
            <line x1={0} x2={layout.gridWidth} y1={top + ROW_H} y2={top + ROW_H} className="stroke-border" strokeWidth={1} />
          </g>
        )
      })}
    </g>
  )
}

export default PrintGrid
